<template>
  <div>
    <sdui-waterfall-search
      v-if="chatId"
      v-model="localNotifications"
      class="scroll-container"
      :search-bar="false"
      :custom-fetch-method="fetchNotifications"
      :has-cached-results="!!localNotifications.length"
      :force-refetch="forceRefetch"
      no-padding
      no-gutters
      skeleton-type="list"
      :skeleton-length="6"
    >
      <template #content>
        <div>
          <div class="d-flex align-items-center justify-content-end w-100 mb-3 p-3">
            <span class="mr-3 mb-0 cursor-pointer"
                  aria-hidden="true"
                  @click="isFiltering = !isFiltering"
            >
              {{ $t('notification_centre.show_only_unread') }}
            </span>
            <SCheckbox
              v-model="isFiltering"
              name="isFiltering"
              checkbox-type="switch"
              :label-text="$t('notification_centre.show_only_unread')"
              @update:model-value="fetchNotifications(1)"
            ></SCheckbox>
          </div>
          <SList>
            <notification-centre-list-item
              v-for="notification in localNotifications"
              :key="notification.uuid"
              :notification="notification"
              @list-item-clicked="executeStaticListItemAction(notification)"
            />
          </SList>
        </div>
      </template>

      <template #noResults>
        <div>
          <sdui-loader v-if="$wait.waiting('fetching-personal-notification-channel')" />
          <div v-else
               class="d-flex flex-column align-items-center mt-6 pt-5"
               data-cy="notification-centre-list-empty"
          >
            <sdui-icon
              size="xl"
              class="icon-check-o mb-4 success-light-bg circle"
              variant="success"
            />
            <div v-if="isFiltering" class="text-center">
              <p class="mb-1 font-weight-bold">{{ $t('notification_centre.no_notifications_filter_active.title') }}</p>
              <p class="mb-0 text-muted">{{ $t('notification_centre.no_notifications_filter_active.subtitle') }}</p>
            </div>
            <div v-else class="text-center">
              <p class="mb-1 font-weight-bold">{{ $t('notification_centre.no_notifications.title') }}</p>
              <p class="mb-0 text-muted">{{ $t('notification_centre.no_notifications.subtitle') }}</p>
            </div>
          </div>
        </div>
      </template>
    </sdui-waterfall-search>

    <!-- Modal for static list item action e.g. EventDetailsModal -->
    <div>
      <component
        :is="staticListItemActionModalComponent.component"
        v-if="staticListItemActionModalComponent.component"
        v-bind="staticListItemActionModalComponent.attrs"
        @close="closeStaticListItemAction"
        @hide="closeStaticListItemAction"
      />
    </div>
  </div>
</template>

<script>
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import { Message } from 'src/store/models/channel/index.js'
import SduiWaterfallSearch from 'src/components/miscellaneous/ui/common/SduiWaterfallSearch.vue'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import NotificationCentreListItem from 'src/components/notificationCentre/NotificationCentreListItem.vue'
import calendarMixin from 'src/mixins/channels/calendar/calendarMixin.js'
import EventDetailsModal from 'src/components/calendar/event/details/EventDetailsModal.vue'
import constants from 'src/config/constants.ts'
import notificationCentreMixin from 'src/mixins/channels/notificationCentreMixin.js'
import SCheckbox from 'src/components/base/SCheckbox.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'NotificationCentreList',
  components: {
    EventDetailsModal,
    SduiLoader,
    SduiWaterfallSearch,
    NotificationCentreListItem,
    SCheckbox,
  },

  mixins: [calendarMixin, notificationCentreMixin],
  data: () => ({
    localNotifications: [],
    isFiltering: false,
    showEventDetailsModal: false,
    notification: null,
    forceRefetch: false,
    displayStaticListItemActionModal: false,
  }),

  computed: {
    notifications() {
      if (this.isFiltering) {
        return Message.query().where('chat_id', Number(this.chatId)).where('is_read', false).with('user').orderBy('created_at').all()
      }
      return Message.query().where('chat_id', Number(this.chatId)).with('user').orderBy('created_at').all()
    },

    staticListItemActionShouldOpenLinkInternal() {
      return this.staticListItemAction?.type === constants.notificationCentre.staticListItemActions.LINK_INTERNAL
    },

    staticListItemActionShouldOpenLinkExternal() {
      return this.staticListItemAction?.type === constants.notificationCentre.staticListItemActions.LINK_EXTERNAL
    },

    staticListItemActionShouldOpenModal() {
      return this.staticListItemAction?.type === constants.notificationCentre.staticListItemActions.OPEN_DETAILS
    },

    staticListItemActionModalComponent() {
      if (!this.staticListItemActionShouldOpenModal) return {}

      // notifications of targetType 'EVENT' and staticListItemAction of 'OPEN_DETAILS' will open the event details modal
      if (this.isCalendarEventNotification) {
        return {
          component: 'EventDetailsModal',
          attrs: {
            eventId: this.notification?.target?.id,
            modelValue: this.displayStaticListItemActionModal,
            notification: this.notification,
            returnFocus: `#notification-centre-list-item-${this.notification.uuid}`,
          },
        }
      }

      // We know we should open a modal, but we don't know which one (unknown notification.target_type)
      return null
    },
  },

  emits: ['close-pop-over'],

  methods: {
    fetchNotifications: waitFor('fetching-notifications', async function (page = 1) {
      return await Message.fetchByChannel(this.chatId, page, this.isFiltering)
    }),

    async executeStaticListItemAction(notification) {
      this.notification = notification
      clyTracking.trackNotification().interaction.openItem()
      // Open a modal
      if (this.staticListItemActionShouldOpenModal) {
        this.displayStaticListItemActionModal = true
        return
      }

      // Redirect internally to route
      if (this.staticListItemActionShouldOpenLinkInternal) {
        await this.$router.push({ path: '/channels' })
        this.$emit('close-pop-over')
        return
      }

      // Redirect externally to url
      if (this.staticListItemActionShouldOpenLinkExternal) {
        window.open(this.staticListItemAction.action, '_blank')
      }
    },

    closeStaticListItemAction() {
      // reset the modal state
      if (!this.staticListItemActionShouldOpenModal) return
      this.displayStaticListItemActionModal = false
    },
  },

  watch: {
    notifications: {
      handler: function (newVal, oldVal) {
        if (this.$compare(oldVal, newVal)) return
        this.localNotifications = this.$clone(newVal).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        this.forceRefetch = !this.forceRefetch
      },

      deep: true,
    },

    isFiltering(newVal) {
      clyTracking.trackNotification().action.toggleUnread({ isFiltering: newVal })
    }
  },

  async mounted() {
    await this.fetchPersonalNotificationChannel()
  },
}
</script>
